import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { inr, formatDate, formatDateTime, todayISO } from "@/lib/utils_pharma";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Printer,
  Calendar,
  AlertCircle,
  Loader2,
  FileText,
  RotateCcw,
  Receipt,
  User,
  Phone,
  Mail,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import PatientReportThermalPrint from "@/components/PatientReportThermalPrint";

function yesterdayISO() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function startOfWeekISO() {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0, 10);
}

function firstOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function PatientSalesReportDialog({
  open,
  onOpenChange,
  patientId,
  initialPreset = "all",
  initialStart = null,
  initialEnd = null,
  shop = null,
}) {
  const [datePreset, setDatePreset] = useState(initialPreset);
  const [start, setStart] = useState(() => initialStart || "");
  const [end, setEnd] = useState(() => initialEnd || "");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportData, setReportData] = useState(null);

  // Shop info fallback
  const [shopInfo, setShopInfo] = useState(shop || {});
  useEffect(() => {
    if (!shop || Object.keys(shop).length === 0) {
      api
        .get("/settings/shop")
        .then((r) => setShopInfo(r.data))
        .catch(() => {});
    } else {
      setShopInfo(shop);
    }
  }, [shop]);

  // Invoice Preview state
  const [previewBill, setPreviewBill] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Thermal Print Preview state
  const [showThermalPreview, setShowThermalPreview] = useState(false);

  // Sync date preset on open or initial props change
  useEffect(() => {
    if (open) {
      if (initialPreset && initialPreset !== "custom") {
        applyDatePreset(initialPreset);
      } else if (initialStart && initialEnd) {
        setDatePreset("custom");
        setStart(initialStart);
        setEnd(initialEnd);
      } else {
        applyDatePreset("all");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPreset, initialStart, initialEnd]);

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    const today = todayISO();
    if (preset === "all") {
      setStart("");
      setEnd("");
    } else if (preset === "today") {
      setStart(today);
      setEnd(today);
    } else if (preset === "yesterday") {
      const y = yesterdayISO();
      setStart(y);
      setEnd(y);
    } else if (preset === "this_week") {
      setStart(startOfWeekISO());
      setEnd(today);
    } else if (preset === "this_month") {
      setStart(firstOfMonthISO());
      setEnd(today);
    } else if (preset === "custom") {
      if (!start || !end) {
        setStart(today);
        setEnd(today);
      }
    }
  };

  const handleCustomStart = (val) => {
    setDatePreset("custom");
    setStart(val);
  };

  const handleCustomEnd = (val) => {
    setDatePreset("custom");
    setEnd(val);
  };

  // Fetch individual patient report
  const fetchReport = async () => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (datePreset !== "all" && start && end) {
        params.start_date = start;
        params.end_date = end;
      }
      const { data } = await api.get(`/reports/patients/${patientId}`, {
        params,
      });
      setReportData(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load patient sales report";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && patientId) {
      fetchReport();
    } else {
      setReportData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId, datePreset, start, end]);

  // View Invoice handler
  const handleViewInvoice = async (billId, billNo) => {
    if (!billId) return;
    setLoadingInvoice(true);
    try {
      const { data: bill } = await api.get(`/bills/${billId}`);
      setPreviewBill(bill);
    } catch (e) {
      toast.error(`Failed to load bill ${billNo || ""}`);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const dateRangeLabel = useMemo(() => {
    if (datePreset === "all") return "All Time";
    if (datePreset === "today") return formatDate(start);
    if (datePreset === "yesterday") return formatDate(start);
    if (start && end && start === end) return formatDate(start);
    if (start && end) return `${formatDate(start)} - ${formatDate(end)}`;
    return "All Time";
  }, [datePreset, start, end]);

  const patient = reportData?.patient || {};
  const summary = reportData?.summary || {
    total_bills: 0,
    total_items: 0,
    total_sales: 0,
    total_returns: 0,
    final_sales: 0,
  };
  const transactions = reportData?.transactions || [];
  const returnTransactions = reportData?.return_transactions || [];

  const paperWidth = ["58mm", "72mm", "80mm"].includes(shopInfo?.thermal_paper_width)
    ? shopInfo.thermal_paper_width
    : "72mm";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-3xl md:max-w-4xl max-h-[92vh] flex flex-col p-0 overflow-hidden"
          data-testid="patient-sales-report-modal"
        >
          {/* 1. MODAL HEADER */}
          <DialogHeader className="px-5 py-3.5 border-b border-border shrink-0 bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <DialogTitle className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
                  <User className="text-primary" size={20} />
                  <span>{patient?.name || "Patient Sales Report"}</span>
                  {patient?.gender && (
                    <Badge variant="outline" className="text-xs font-normal">
                      {patient?.age ? `${patient.age}y / ` : ""}{patient.gender}
                    </Badge>
                  )}
                </DialogTitle>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {patient?.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={12} /> {patient.phone}
                    </span>
                  )}
                  {patient?.email && (
                    <span className="flex items-center gap-1">
                      <Mail size={12} /> {patient.email}
                    </span>
                  )}
                  {patient?.address && (
                    <span className="truncate max-w-xs text-muted-foreground/80">
                      • {patient.address}
                    </span>
                  )}
                </div>
              </div>

              {/* Header Action: Thermal Print */}
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowThermalPreview(true)}
                  disabled={loading || !reportData}
                  className="gap-1.5"
                  data-testid="print-patient-report-btn"
                >
                  <Printer size={15} />
                  <span>Print Report</span>
                  <span className="text-[10px] text-muted-foreground">({paperWidth})</span>
                </Button>
              </div>
            </div>
          </DialogHeader>

          {/* 2. DATE FILTER BAR & CONTROLS */}
          <div className="px-5 py-2.5 bg-muted/40 border-b border-border/80 flex flex-wrap items-center justify-between gap-2 shrink-0">
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { id: "all", label: "All" },
                { id: "today", label: "Today" },
                { id: "yesterday", label: "Yesterday" },
                { id: "this_week", label: "This Week" },
                { id: "this_month", label: "This Month" },
                { id: "custom", label: "Custom" },
              ].map((p) => (
                <Button
                  key={p.id}
                  variant={datePreset === p.id ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-2.5"
                  onClick={() => applyDatePreset(p.id)}
                  data-testid={`detail-preset-${p.id}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            {datePreset !== "all" ? (
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <Label className="text-[11px] text-muted-foreground">From</Label>
                  <Input
                    type="date"
                    value={start}
                    onChange={(e) => handleCustomStart(e.target.value)}
                    className="h-7 text-xs w-32 bg-white"
                    data-testid="detail-start-date"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-[11px] text-muted-foreground">To</Label>
                  <Input
                    type="date"
                    value={end}
                    onChange={(e) => handleCustomEnd(e.target.value)}
                    className="h-7 text-xs w-32 bg-white"
                    data-testid="detail-end-date"
                  />
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <Calendar size={13} className="text-muted-foreground/70" />
                <span>Showing complete history (All Time)</span>
              </div>
            )}
          </div>

          {/* 3. SCROLLABLE BODY */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {error && (
              <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle size={16} />
                <span>{error}</span>
                <Button size="sm" variant="ghost" onClick={fetchReport} className="ml-auto h-7 text-xs">
                  Retry
                </Button>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center p-8 text-sm text-muted-foreground gap-2">
                <Loader2 size={18} className="animate-spin text-primary" />
                <span>Loading patient sales data...</span>
              </div>
            )}

            {!loading && reportData && (
              <>
                {/* 4. SUMMARY CARDS */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-md border border-border p-3 bg-white" data-testid="detail-card-bills">
                    <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground truncate">
                      Total Bills
                    </div>
                    <div className="mt-1 text-xl font-semibold tabular-nums" style={{ fontFamily: "Chivo" }}>
                      {summary.total_bills}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Items: {summary.total_items}
                    </div>
                  </div>

                  <div className="rounded-md border border-border p-3 bg-white" data-testid="detail-card-total-sales">
                    <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground truncate">
                      Total Sales
                    </div>
                    <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900" style={{ fontFamily: "Chivo" }}>
                      {inr(summary.total_sales)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Before returns</div>
                  </div>

                  <div className="rounded-md border border-border p-3 bg-white" data-testid="detail-card-total-returns">
                    <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground truncate">
                      Total Returns
                    </div>
                    <div
                      className={`mt-1 text-xl font-semibold tabular-nums ${
                        summary.total_returns > 0 ? "text-amber-600" : "text-slate-900"
                      }`}
                      style={{ fontFamily: "Chivo" }}
                    >
                      {inr(summary.total_returns)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Returned amount</div>
                  </div>

                  <div className="rounded-md border border-border p-3 bg-white" data-testid="detail-card-final-sales">
                    <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground truncate">
                      Final Sales
                    </div>
                    <div
                      className={`mt-1 text-xl font-semibold tabular-nums ${
                        summary.final_sales < 0
                          ? "text-red-600 font-bold"
                          : summary.final_sales > 0
                          ? "text-emerald-700"
                          : "text-slate-900"
                      }`}
                      style={{ fontFamily: "Chivo" }}
                    >
                      {inr(summary.final_sales)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">After returns</div>
                  </div>
                </div>

                {/* 5. SALES TRANSACTIONS TABLE */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5" style={{ fontFamily: "Chivo" }}>
                      <Receipt size={16} className="text-muted-foreground" />
                      Sales Transactions
                    </h3>
                    <span className="text-xs text-muted-foreground font-mono">
                      {transactions.length} {transactions.length === 1 ? "bill" : "bills"}
                    </span>
                  </div>

                  <div className="rounded-md border border-border bg-white overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/40">
                          <TableHead className="w-28">Bill No</TableHead>
                          <TableHead className="w-24">Date</TableHead>
                          <TableHead className="text-center w-16">Items</TableHead>
                          <TableHead className="w-24">Payment</TableHead>
                          <TableHead className="text-right w-24">Total Sales</TableHead>
                          <TableHead className="text-right w-24">Returns</TableHead>
                          <TableHead className="text-right w-24 font-bold">Final Sales</TableHead>
                          <TableHead className="text-right w-28">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-6 text-xs text-muted-foreground">
                              {datePreset === "all"
                                ? "No sales bills found for this patient."
                                : "No sales bills found for this patient during the selected period."}
                            </TableCell>
                          </TableRow>
                        ) : (
                          transactions.map((tx) => (
                            <TableRow key={tx.bill_id} className="hover:bg-muted/20" data-testid={`tx-row-${tx.bill_no}`}>
                              <TableCell className="font-mono font-medium text-xs">
                                {tx.bill_no}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {formatDate(tx.sale_date || tx.created_at)}
                              </TableCell>
                              <TableCell className="text-center text-xs">
                                {tx.item_count}
                              </TableCell>
                              <TableCell className="text-xs">
                                <Badge variant="outline" className="font-normal text-[11px]">
                                  {tx.payment_mode === "Split" ? "Split" : tx.payment_mode}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right text-xs tabular-nums">
                                {inr(tx.total_sales)}
                              </TableCell>
                              <TableCell className="text-right text-xs tabular-nums text-amber-600">
                                {tx.in_range_return_amount > 0 ? `-${inr(tx.in_range_return_amount)}` : "₹0.00"}
                              </TableCell>
                              <TableCell className="text-right text-xs tabular-nums font-semibold">
                                {inr(tx.final_sales)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs text-primary hover:text-primary/80 gap-1 px-2"
                                  onClick={() => handleViewInvoice(tx.bill_id, tx.bill_no)}
                                  disabled={loadingInvoice}
                                  data-testid={`view-invoice-${tx.bill_no}`}
                                >
                                  <FileText size={13} />
                                  <span>View</span>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* 6. RETURN TRANSACTIONS SECTION */}
                <div className="space-y-2 pt-2 border-t border-border/60">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5" style={{ fontFamily: "Chivo" }}>
                      <RotateCcw size={15} className="text-amber-600" />
                      {datePreset === "all" ? "Returns - All Time" : "Returns in Selected Period"}
                    </h3>
                    <span className="text-xs text-muted-foreground font-mono">
                      {returnTransactions.length} {returnTransactions.length === 1 ? "return" : "returns"}
                    </span>
                  </div>

                  <div className="rounded-md border border-border bg-white overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/40">
                          <TableHead className="w-24">Date</TableHead>
                          <TableHead className="w-28">Bill No</TableHead>
                          <TableHead className="w-24">Return Type</TableHead>
                          <TableHead>Return Mode</TableHead>
                          <TableHead className="text-right w-28">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {returnTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-5 text-xs text-muted-foreground">
                              {datePreset === "all"
                                ? "No returns processed for this patient."
                                : "No returns processed for this patient during the selected period."}
                            </TableCell>
                          </TableRow>
                        ) : (
                          returnTransactions.map((ret, idx) => (
                            <TableRow key={ret.return_id || idx} className="hover:bg-muted/20" data-testid={`return-row-${ret.bill_no}`}>
                              <TableCell className="text-xs text-muted-foreground">
                                {formatDate(ret.return_date || ret.created_at)}
                              </TableCell>
                              <TableCell className="font-mono text-xs font-medium">
                                {ret.bill_no || "—"}
                              </TableCell>
                              <TableCell className="text-xs">
                                <Badge
                                  variant="outline"
                                  className={`text-[11px] font-normal ${
                                    ret.return_type === "Exchange"
                                      ? "border-blue-300 text-blue-700 bg-blue-50"
                                      : "border-amber-300 text-amber-700 bg-amber-50"
                                  }`}
                                >
                                  {ret.return_type || "Refund"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {ret.return_mode || "Single Item Return"}
                              </TableCell>
                              <TableCell className="text-right text-xs font-semibold tabular-nums text-amber-600">
                                {inr(ret.refund_amount)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* 7. ALL-EMPTY FALLBACK NOTICE */}
                {transactions.length === 0 && returnTransactions.length === 0 && (
                  <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground bg-muted/20">
                    <Info className="mx-auto mb-2 opacity-50 text-muted-foreground" size={24} />
                    {datePreset === "all"
                      ? "No sales or returns found for this patient."
                      : "No sales or returns found for this patient during the selected period."}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* REUSED INVOICE PREVIEW DIALOG */}
      <InvoicePreviewDialog
        open={!!previewBill}
        onOpenChange={(o) => !o && setPreviewBill(null)}
        bill={previewBill}
        shop={shopInfo}
      />

      {/* THERMAL PRINT PREVIEW MODAL */}
      {showThermalPreview && (
        <Dialog open={showThermalPreview} onOpenChange={setShowThermalPreview}>
          <DialogContent
            className="sm:max-w-md max-h-[90vh] flex flex-col p-0 overflow-hidden receipt-print-dialog"
            data-testid="patient-thermal-preview-dialog"
          >
            <DialogHeader className="px-5 py-3.5 border-b border-border shrink-0 bg-white no-print">
              <DialogTitle className="flex items-center justify-between text-base">
                <span>Thermal Report · {paperWidth}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {patient?.name}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div className="bg-muted/40 rounded-lg p-3 border border-border/60 flex justify-center receipt-preview-shell">
                <PatientReportThermalPrint
                  patient={patient}
                  summary={summary}
                  transactions={transactions}
                  returnTransactions={returnTransactions}
                  periodLabel={dateRangeLabel}
                  shop={shopInfo}
                  preview
                />
              </div>
              <div className="text-[11px] text-muted-foreground text-center no-print">
                Formatted for {paperWidth} thermal roll printer.
              </div>
            </div>

            <div className="px-5 py-3 border-t border-border shrink-0 bg-white flex justify-end gap-2 no-print">
              <Button variant="outline" size="sm" onClick={() => setShowThermalPreview(false)}>
                Close
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  window.print();
                }}
                data-testid="confirm-thermal-print-btn"
              >
                <Printer size={15} />
                <span>Print</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
